import type { DeliveryMethod } from "@au-schein-de/au-ui-library";
import type { PharmacyPickerItem } from "~/features/products/pharmacy-picker/pharmacy-picker.types";
import type { OfferCardDeliveryBadge } from "~/features/products/product-details-vendor-select-trigger.component";

export type VendorOptionForPicker = {
	id: string;
	title: string;
	address?: string;
	featured?: boolean;
	combinedPaymentEnabled?: boolean;
	distance?: number;
	freeFromCents?: number;
	deliveryMethods?: DeliveryMethod[];
	deliveryMethodLabels?: Partial<Record<DeliveryMethod, string | undefined>>;
	isGroup?: boolean;
};

export const minEnabledFreeFromCents = (
	methods:
		| ReadonlyArray<{ enabled?: boolean; freeFrom?: number | null }>
		| undefined,
): number | undefined => {
	const values = (methods ?? [])
		.filter(
			(method) =>
				method.enabled === true && typeof method.freeFrom === "number",
		)
		.map((method) => method.freeFrom as number);
	if (values.length === 0) return undefined;
	return Math.min(...values);
};

const buildDeliveryBadges = (
	deliveryMethods: DeliveryMethod[] | undefined,
	labels: Partial<Record<DeliveryMethod, string | undefined>> | undefined,
): OfferCardDeliveryBadge[] => {
	if (!deliveryMethods?.length) return [];
	return deliveryMethods.map((kind) => ({
		kind: kind as OfferCardDeliveryBadge["kind"],
		label: labels?.[kind],
	}));
};

export const mapVendorOptionToPickerItem = (
	vendor: VendorOptionForPicker,
): PharmacyPickerItem => ({
	id: vendor.id,
	name: vendor.title,
	address: vendor.address,
	featured: vendor.featured ?? false,
	combinedPaymentEnabled: vendor.combinedPaymentEnabled ?? false,
	distanceKm: vendor.distance,
	...(typeof vendor.freeFromCents === "number"
		? { freeFromCents: vendor.freeFromCents }
		: {}),
	deliveryBadges: buildDeliveryBadges(
		vendor.deliveryMethods,
		vendor.deliveryMethodLabels,
	),
});

export const filterIndividualPickerPharmacies = (
	options: VendorOptionForPicker[],
): PharmacyPickerItem[] =>
	options.filter((o) => !o.isGroup).map(mapVendorOptionToPickerItem);

/**
 * Strictly drops pharmacies farther than `radiusKm` — including search matches.
 * Items with an unknown distance are kept, so the list isn't emptied before a
 * location (postal code) is set. Used for map delivery types (pickup/courier).
 */
export const capPharmaciesByRadiusKm = (
	items: PharmacyPickerItem[],
	radiusKm: number,
): PharmacyPickerItem[] =>
	items.filter((p) => p.distanceKm == null || p.distanceKm <= radiusKm);
